import { useContext } from 'react';
import type { FieldRenderProps } from 'react-final-form';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import Input, { type InputProps } from './Input';

type ReactFinalFormProps = FieldRenderProps<string, HTMLInputElement>;

export interface FormInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'name' | 'error'> {
  input: ReactFinalFormProps['input'];
  meta: ReactFinalFormProps['meta'];
  localizeErrorMessages?: boolean;
  /** Suppress the inline error row under the input (e.g. when errors are shown in a top-level container). */
  hideInlineError?: boolean;
  onEnterPress?: (event: React.KeyboardEvent<HTMLInputElement>, value: string) => void;
  dataTestId?: string;
}

function resolveError(
  error: unknown,
  localize: boolean,
  getLocalizedText: (key: string, arg?: string) => string,
): React.ReactNode {
  if (!error) return undefined;

  if (Array.isArray(error)) {
    const messages = error.map((item) => {
      if (Array.isArray(item)) {
        return localize ? getLocalizedText(item[0], item[1]?.[0]) : item[0];
      }
      return localize ? getLocalizedText(item) : item;
    });
    return messages.length === 1 ? messages[0] : messages.join(' ');
  }

  return localize && typeof error === 'string'
    ? getLocalizedText(error)
    : (error as React.ReactNode);
}

export default function FormInput({
  input,
  meta,
  localizeErrorMessages = true,
  hideInlineError = false,
  onEnterPress,
  onKeyDown,
  dataTestId,
  ...rest
}: FormInputProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);

  const isInvalid = meta.touched && meta.invalid;
  const error =
    isInvalid && !hideInlineError
      ? resolveError(meta.error, localizeErrorMessages, getLocalizedText)
      : undefined;

  return (
    <Input
      {...input}
      data-testid={dataTestId}
      invalid={isInvalid}
      error={error}
      onKeyDown={(event) => {
        if (onEnterPress && event.key === 'Enter') {
          onEnterPress(event, input.value);
        }
        onKeyDown?.(event);
      }}
      {...rest}
    />
  );
}
